import React, { FC, RefObject, useState } from 'react';
import classnames from 'classnames';
import { domAnimation, LazyMotion, m } from 'framer-motion';
import {
  WarningIcon,
  ErrorIcon,
  BoltIcon,
  CloseIcon,
} from 'components/icon/Icon';

import s from './Notification.module.scss';

const c = classnames.bind(s);

type NotificationProps = {
  title?: string;
  type: 'success' | 'warning' | 'error';
  onClose?: () => void;
  notificationRef?: RefObject<HTMLDivElement>;
  children?: React.ReactNode;
};

const alertVariation = {
  hidden: {
    opacity: 0,
    x: 20,
  },
  visible: {
    opacity: 1,
    x: 0,
    transition: { type: 'spring' },
  },
};

export type NotificationMessage = {
  title?: string;
  message: string;
  type?: 'success' | 'warning' | 'error';
};

const Notification: FC<NotificationProps> = ({
  type,
  onClose,
  title,
  notificationRef,
  children,
}) => {
  const [closing, setClosing] = useState(false);
  const closeNotification = () => {
    setClosing(true);
    onClose && onClose();
  };

  return (
    <LazyMotion features={domAnimation}>
      <m.div
        ref={notificationRef}
        className={s.notification}
        initial={type === 'error' ? 'hidden' : 'visible'}
        animate={type === 'error' && 'visible'}
        variants={alertVariation}
      >
        <div
          className={c(s.notification__icon, {
            [s.success]: type === 'success',
            [s.warning]: type === 'warning',
            [s.error]: type === 'error',
          })}
        >
          {type === 'warning' ? (
            <WarningIcon />
          ) : type === 'error' ? (
            <ErrorIcon />
          ) : (
            <BoltIcon />
          )}
        </div>
        <div className={s.notification__text}>
          {title && <strong className={s.notification__title}>{title}</strong>}
          {children}
        </div>
        {onClose && (
          <button
            className={s.notification__close}
            type="button"
            onClick={closeNotification}
          >
            <div className={s.notification__closeIcon}>
              <CloseIcon />
            </div>
            Loka
          </button>
        )}
      </m.div>
    </LazyMotion>
  );
};

export default Notification;
